import { useCallback, useMemo, useState } from 'react';

import { useIntl } from '@futrli/localisation';

import {
  SortConfig,
  buildSortOptions,
  parseSortOptionValue,
  toSortOptionValue,
  useFilteredData,
  FilterStep,
  useListFilterPersistence,
  readPersistedFilters
} from '../../../../utils/list-filters';
import { REPORTS_SORT_COLUMNS, REPORTS_COMPARATORS, DEFAULT_REPORTS_SORT, HasName, HasCreated } from './sort-config';

export type StatusFilterValue = 'all' | 'published' | 'draft' | 'shared' | 'private';
export type TypeFilterValue = 'all' | 'portrait' | 'landscape' | 'dashboard';

interface HasSharingStatus {
  permission_info?: {
    sharing_status?: string;
  };
}

const getValidStatusFilter = (statusFilter: string, isResourceSharingEnabled: boolean): StatusFilterValue => {
  const validValues: StatusFilterValue[] = isResourceSharingEnabled
    ? ['all', 'shared', 'private']
    : ['all', 'published', 'draft'];
  return validValues.includes(statusFilter as StatusFilterValue) ? (statusFilter as StatusFilterValue) : 'all';
};

interface StatusOrientationFilterConfig<T extends HasName & HasCreated> {
  /** Raw data to filter (should already exclude dashboard items) */
  data: T[];
  /** Extracts the status field from an item (e.g. item => item.status) */
  statusAccessor: (item: T) => string;
  /** The API status value that maps to "Published" in the UI — 'live' for reports, 'published' for templates */
  publishedValue: string;
  /** Extracts the name field used for search matching */
  nameAccessor: (item: T) => string;
  /** Extracts the orientation field used for type filtering */
  orientationAccessor: (item: T) => string;
  /** localStorage key for persisting filter state (unique per tab) */
  storageKey: string;
  /** Organisation ID used to scope persisted filters */
  organisationId: string;
  /** Whether the sharing filter should replace the publication status filter */
  isResourceSharingEnabled?: boolean;
  /** Extracts the sharing status from an item when resource sharing is enabled */
  sharingStatusAccessor?: (item: T & HasSharingStatus) => string | undefined;
}

/**
 * Shared filter + sort hook for My Reports and Report Templates tabs.
 *
 * Both tabs have identical filter UI (Search + Status toggle + Type toggle + Sort)
 * but differ in their data types and status field mapping.
 * This hook is configured via accessors so it works generically with both
 * LiveReportList and ReportTemplateList.
 *
 * Uses useFilteredData pipeline: search → status custom filter → orientation custom filter → sort.
 * No localStorage persistence, no React Context — all state is local.
 */
export const useStatusOrientationFilter = <T extends HasName & HasCreated>({
  data,
  statusAccessor,
  publishedValue,
  nameAccessor,
  orientationAccessor,
  storageKey,
  organisationId,
  isResourceSharingEnabled = false,
  sharingStatusAccessor
}: StatusOrientationFilterConfig<T>) => {
  const intl = useIntl();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(() => {
    const persisted = readPersistedFilters(storageKey, organisationId);
    return getValidStatusFilter(
      (persisted?.filters?.statusFilter as StatusFilterValue) ?? 'all',
      isResourceSharingEnabled
    );
  });
  const [typeFilter, setTypeFilter] = useState<TypeFilterValue>(() => {
    const persisted = readPersistedFilters(storageKey, organisationId);
    return (persisted?.filters?.typeFilter as TypeFilterValue) ?? 'all';
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
    const persisted = readPersistedFilters(storageKey, organisationId);
    return persisted?.sort ?? DEFAULT_REPORTS_SORT;
  });

  const filters = useMemo(() => ({ statusFilter, typeFilter }), [statusFilter, typeFilter]);

  const handleRestore = useCallback(
    (
      restoredFilters: { statusFilter: StatusFilterValue; typeFilter: TypeFilterValue },
      restoredSort: SortConfig | undefined
    ) => {
      setStatusFilter(getValidStatusFilter(restoredFilters.statusFilter, isResourceSharingEnabled));
      setTypeFilter(restoredFilters.typeFilter);
      if (restoredSort) {
        setSortConfig(restoredSort);
      }
    },
    [isResourceSharingEnabled]
  );

  useListFilterPersistence({
    storageKey,
    organisationId,
    filters,
    sort: sortConfig,
    onRestore: handleRestore
  });

  const sortOptions = useMemo(() => buildSortOptions(REPORTS_SORT_COLUMNS, intl), [intl]);
  const sortValue = useMemo(() => toSortOptionValue(sortConfig), [sortConfig]);

  const handleSortChange = useCallback((value: string) => {
    setSortConfig(parseSortOptionValue(value));
  }, []);

  const searchAccessors = useMemo(() => [nameAccessor], [nameAccessor]);

  const filterSteps: FilterStep<T>[] = useMemo(
    () => [
      {
        type: 'custom' as const,
        apply: (items: T[]) => {
          if (statusFilter === 'all') {
            return items;
          }
          if (isResourceSharingEnabled) {
            if (statusFilter === 'shared' || statusFilter === 'private') {
              return items.filter(item => sharingStatusAccessor?.(item as T & HasSharingStatus) === statusFilter);
            }
            return items;
          }
          if (statusFilter === 'published') {
            return items.filter(item => statusAccessor(item) === publishedValue);
          }
          if (statusFilter === 'draft') {
            return items.filter(item => statusAccessor(item) === 'draft');
          }
          return items;
        }
      },
      {
        type: 'custom' as const,
        apply: (items: T[]) => {
          if (typeFilter === 'all') {
            return items;
          }
          return items.filter(item => orientationAccessor(item) === typeFilter);
        }
      }
    ],
    [
      statusFilter,
      typeFilter,
      statusAccessor,
      publishedValue,
      orientationAccessor,
      isResourceSharingEnabled,
      sharingStatusAccessor
    ]
  );

  const filteredData = useFilteredData<T>({
    allData: data,
    searchTerm,
    searchAccessors,
    filterSteps,
    sortConfig,
    comparators: REPORTS_COMPARATORS
  });

  return {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    sortValue,
    sortOptions,
    handleSortChange
  };
};
